import React, { memo, useCallback, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Paper, Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PieChart from '../PieChart';
import LabelChartSkeleton from '../LabelChartSkeleton';
import { LabelNoData } from '../LabelNoData';
import { PieChartData } from '../utils';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    maxHeight: theme.spacing(32.5),
    minHeight: theme.spacing(32.5),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  typography: {
    color: theme.palette.text.secondary
  },
  headingContainer: {
    minHeight: theme.spacing(5)
  },
  removeBtn: {
    visibility: ({ show }: { show: boolean }) => (show ? 'visible' : 'hidden'),
    opacity: ({ show }: { show: boolean }) => (show ? 1 : 0),
    '-webkit-transition': 'opacity 0.5s ease-in-out',
    '-moz-transition': 'opacity 0.5s ease-in-out',
    '-ms-transition': 'opacity 0.5s ease-in-out',
    '-o-transition': 'opacity 0.5s ease-in-out',
    transition: 'opacity 0.5s ease-in-out'
  }
}));

type OEFChartProps = {
  labelId: string;
  labelName: string;
  handleRemove: (labelId: string) => void;
  loading: boolean;
  isEmpty: boolean;
  dataset: PieChartData;
  setGetCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
};

export const OEFChart: React.FC<OEFChartProps> = ({
  labelId,
  labelName,
  handleRemove,
  dataset,
  loading,
  isEmpty,
  setGetCurrentPage
}: OEFChartProps) => {
  const [show, setShow] = useState<boolean>(false);
  const showIcon = useCallback(() => setShow(true), []);
  const closeIcon = useCallback(() => setShow(false), []);
  const classes = useStyles({ show });

  const handleClickRemove = useCallback(() => {
    handleRemove(labelId);
  }, [handleRemove, labelId]);

  return (
    <Paper
      elevation={3}
      data-testid={`source-by-OEF-${labelName}-chart-container`}
      className={classes.container}
      onMouseOver={showIcon}
      onMouseLeave={closeIcon}
    >
      <Grid container direction="column">
        <Grid
          item
          container
          justifyContent={'space-between'}
          className={classes.headingContainer}
        >
          <Grid item>
            <Typography
              className={classes.typography}
              variant="h6"
              data-testid="oef-chart-title"
            >
              {labelName}
            </Typography>
          </Grid>
          <Grid item>
            {show && (
              <IconButton
                aria-label="remove"
                onClick={handleClickRemove}
                data-testid={`remove-${labelName}-${labelId}`}
                className={classes.removeBtn}
                color="secondary"
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item>
          {loading ? (
            <LabelChartSkeleton />
          ) : isEmpty ? (
            <LabelNoData />
          ) : (
            <PieChart
              data={dataset}
              labelType={labelName}
              setGetCurrentPage={setGetCurrentPage}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};
export default memo(OEFChart);
